import { useComputed, useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import { useEffect, useRef } from "preact/hooks";
import {
  IcoCheck,
  IcoChevronLeft,
  IcoChevronRight,
  IcoSpinner,
  IcoUndo,
  IcoX,
} from "../../components/icons";
import {
  CommentButton,
  CommentThread,
  PriorityItem,
  PrioritySection,
  ReactionBar,
} from "../../components/ui";
import { ErrorDialog } from "../../components/ui/error-dialog";
import {
  RatingTrendChart,
  type RatingTrendPoint,
} from "../../components/ui/rating-sparkline";
import { decodeMentionsToHtml } from "../../components/ui/rich-text-mention-extension";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type {
  BrandColor,
  FormAnswer,
  Priority,
  ReactionSummary,
  User,
  ValueRecognition,
} from "../../types";
import { isAdmin as checkIsAdmin } from "../../types";
import { formatDateWithTime, formatPeriodRange } from "../../utils/format";
import {
  getMondayFromWeekKey,
  getWeekKeyFromDate,
} from "../../utils/period-grouping";
import {
  type FullSubmissionData,
  loadFullSubmission,
  type QuestionInfo,
  type ReviewQueueItem,
} from "./review-shared";

type UndoEntry = {
  submissionId: string;
  data: FullSubmissionData;
  queueIndex: number;
};

type FocusableItem =
  | {
      type: "priority";
      period: "previous" | "current";
      priority: Priority;
      id: string;
    }
  | {
      type: "answer";
      question: QuestionInfo;
      answer: FormAnswer | null;
      id: string;
    };

type ReviewPageState = {
  currentUser: User;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  weekKey: string;
  queue: ReviewQueueItem[];
  firstSubmission: FullSubmissionData;
  hasReports: boolean;
};

// ============================================================================
// Loader
// ============================================================================

export async function load(args: LoaderArgs): Promise<ReviewPageState> {
  const orgId = args.params.orgId ?? "";
  const requestedWeek = args.url.searchParams.get("week");
  const scope = args.url.searchParams.get("scope"); // "team" = direct reports only

  // Stage 1: Validate auth before making data calls
  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);

  if (!authResult.user) throw new RedirectError("/login");

  const org = orgsResult.organizations.find((o) => o.id === orgId);
  if (!org) throw new RedirectError("/");

  const isOrgAdmin = checkIsAdmin(org.role);

  // Default to the most recently completed form period for review
  const currentWeekKey = getWeekKeyFromDate(new Date());
  let weekKey: string;
  if (requestedWeek) {
    weekKey = requestedWeek;
  } else {
    const reviewPeriod = await rpc.team.getReviewPeriod({
      workspaceId: orgId,
    });
    weekKey = reviewPeriod.periodStart
      ? getWeekKeyFromDate(new Date(reviewPeriod.periodStart))
      : currentWeekKey;
  }
  const periodStart = getMondayFromWeekKey(weekKey);

  // Stage 2: Data calls (auth validated)
  // Admin-managers default to direct reports; admin-only (no reports) see everyone.
  // Explicit scope=team forces direct reports; any other scope value forces all members.
  const effectiveScope =
    scope ?? (isOrgAdmin && org.hasReports ? "team" : null);
  const useAllMembers = isOrgAdmin && effectiveScope !== "team";
  const teamResult = await rpc.team.getMyTeam({
    workspaceId: orgId,
    periodStart: periodStart.toISOString(),
    allMembers: useAllMembers || undefined,
  });

  // Build queue of unreviewed submissions
  const queue: ReviewQueueItem[] = teamResult.team
    .filter((m) => m.latestSubmission && m.latestSubmission.reviewedAt === null)
    .map((m) => ({
      submissionId: m.latestSubmission!.id,
      formId: m.latestSubmission!.formId,
      memberName: m.name || m.email,
      memberProfilePictureUrl: m.profilePictureUrl ?? null,
      formTitle: m.latestSubmission!.formTitle ?? "Check-in",
    }));

  if (queue.length === 0) {
    throw new RedirectError(`/orgs/${orgId}/team?week=${weekKey}`);
  }

  // Load first submission
  const firstSubmission = await loadFullSubmission(orgId, queue[0]!);

  return {
    currentUser: authResult.user as User,
    orgId,
    orgName: org.name,
    brandColor: (org.brandColor ?? "indigo") as BrandColor,
    weekKey,
    queue,
    firstSubmission,
    hasReports: teamResult.hasReports,
  };
}

// ============================================================================
// Page Component
// ============================================================================

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Review");
  const {
    orgId,
    brandColor,
    weekKey,
    queue: initialQueue,
    firstSubmission,
  } = state.value;

  // Core state
  const queue = useSignal<ReviewQueueItem[]>(initialQueue);
  const currentIndex = useSignal(0);
  const currentData = useSignal<FullSubmissionData>(firstSubmission);
  const isTransitioning = useSignal(false);
  const animationKey = useSignal(0);

  // Prefetch cache
  const prefetchCache = useRef(new Map<string, FullSubmissionData>());

  // Undo stack
  const undoStack = useSignal<UndoEntry[]>([]);
  const showUndoToast = useSignal(false);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus state
  const focusedIndex = useSignal(0);
  const showReactionPicker = useSignal(false);
  const openCommentId = useSignal<string | null>(null);

  // All done state
  const allDone = useSignal(false);

  // Marking reviewed
  const markingReviewed = useSignal(false);

  // Build focusable items list
  const focusableItems = useComputed<FocusableItem[]>(() => {
    const data = currentData.value;
    const items: FocusableItem[] = [];

    // Rating answers first, then other answers, then priorities
    const ratingQs = data.questions.filter((q) => q.type === "rating");
    const otherQs = data.questions.filter((q) => q.type !== "rating");

    for (const q of ratingQs) {
      const answer =
        data.submission.answers.find((a) => a.questionId === q.id) ?? null;
      items.push({ type: "answer", question: q, answer, id: `answer-${q.id}` });
    }
    for (const q of otherQs) {
      const answer =
        data.submission.answers.find((a) => a.questionId === q.id) ?? null;
      items.push({ type: "answer", question: q, answer, id: `answer-${q.id}` });
    }

    if (data.priorities) {
      for (const p of data.priorities.previousPeriod) {
        items.push({
          type: "priority",
          period: "previous",
          priority: p,
          id: `prev-${p.id}`,
        });
      }
      for (const p of data.priorities.currentPeriod) {
        items.push({
          type: "priority",
          period: "current",
          priority: p,
          id: `curr-${p.id}`,
        });
      }
    }

    return items;
  });

  // Refs for scroll-into-view
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Scroll to top of main content when advancing to next submission
  const mainContainerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    mainContainerRef.current?.scrollTo(0, 0);
  }, [animationKey.value]);

  // Scroll focused item into view
  useEffect(() => {
    const items = focusableItems.value;
    if (items.length === 0) return;
    const idx = Math.min(focusedIndex.value, items.length - 1);
    const item = items[idx];
    if (!item) return;
    const el = itemRefs.current.get(item.id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusedIndex.value, animationKey.value]);

  // Prefetch next submissions
  useEffect(() => {
    const q = queue.value;
    const idx = currentIndex.value;
    for (let i = 1; i <= 2; i++) {
      const nextIdx = idx + i;
      if (nextIdx < q.length) {
        const nextItem = q[nextIdx]!;
        if (!prefetchCache.current.has(nextItem.submissionId)) {
          loadFullSubmission(orgId, nextItem)
            .then((data) => {
              prefetchCache.current.set(nextItem.submissionId, data);
              // Evict old entries to keep cache small
              if (prefetchCache.current.size > 4) {
                const firstKey = prefetchCache.current.keys().next().value;
                if (firstKey) prefetchCache.current.delete(firstKey);
              }
            })
            .catch(() => {});
        }
      }
    }
  }, [currentIndex.value, orgId]);

  // Advance to next submission
  const advanceToNext = async () => {
    if (isTransitioning.value || markingReviewed.value) return;

    const data = currentData.value;
    markingReviewed.value = true;

    try {
      await rpc.submissions.markReviewed({ submissionId: data.submission.id });

      // Push to undo stack
      undoStack.value = [
        ...undoStack.value,
        {
          submissionId: data.submission.id,
          data,
          queueIndex: currentIndex.value,
        },
      ];

      // Show undo toast
      showUndoToast.value = true;
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(() => {
        showUndoToast.value = false;
      }, 3000);

      const nextIdx = currentIndex.value + 1;
      if (nextIdx >= queue.value.length) {
        allDone.value = true;
        return;
      }

      // Transition to next
      isTransitioning.value = true;
      const nextItem = queue.value[nextIdx]!;
      const cached = prefetchCache.current.get(nextItem.submissionId);

      if (cached) {
        currentData.value = cached;
        prefetchCache.current.delete(nextItem.submissionId);
      } else {
        const loaded = await loadFullSubmission(orgId, nextItem);
        currentData.value = loaded;
      }

      currentIndex.value = nextIdx;
      focusedIndex.value = 0;
      showReactionPicker.value = false;
      openCommentId.value = null;
      animationKey.value += 1;
    } catch (err) {
      showError(
        "Failed to mark reviewed",
        err instanceof Error ? err.message : "Please try again.",
      );
    } finally {
      markingReviewed.value = false;
      isTransitioning.value = false;
    }
  };

  // Undo last review
  const handleUndo = async () => {
    const stack = undoStack.value;
    if (stack.length === 0) return;

    const entry = stack[stack.length - 1]!;
    undoStack.value = stack.slice(0, -1);
    showUndoToast.value = false;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);

    try {
      await rpc.submissions.unmarkReviewed({
        submissionId: entry.submissionId,
      });

      // Go back
      isTransitioning.value = true;
      allDone.value = false;
      currentData.value = entry.data;
      currentIndex.value = entry.queueIndex;
      focusedIndex.value = 0;
      showReactionPicker.value = false;
      openCommentId.value = null;
      animationKey.value += 1;
    } catch (err) {
      showError(
        "Failed to undo review",
        err instanceof Error ? err.message : "Please try again.",
      );
    } finally {
      isTransitioning.value = false;
    }
  };

  // Exit back to team page
  const exitReview = () => {
    navigateTo(`/orgs/${orgId}/team?week=${weekKey}`);
  };

  // Keyboard handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      const isInput =
        tagName === "input" ||
        tagName === "textarea" ||
        target.contentEditable === "true" ||
        target.closest(".tiptap");

      // Escape always works
      if (e.key === "Escape") {
        e.preventDefault();
        if (showReactionPicker.value) {
          showReactionPicker.value = false;
        } else if (openCommentId.value) {
          openCommentId.value = null;
        } else {
          exitReview();
        }
        return;
      }

      // Don't handle other keys when in input
      if (isInput) return;

      // Don't handle if all done
      if (allDone.value) {
        if (e.key === "u") {
          e.preventDefault();
          handleUndo();
        }
        return;
      }

      const items = focusableItems.value;

      switch (e.key) {
        case "j":
        case "ArrowDown": {
          e.preventDefault();
          if (focusedIndex.value >= items.length - 1) {
            // Past last item — mark reviewed and advance
            advanceToNext();
          } else {
            focusedIndex.value = Math.min(
              focusedIndex.value + 1,
              items.length - 1,
            );
          }
          showReactionPicker.value = false;
          break;
        }
        case "k":
        case "ArrowUp": {
          e.preventDefault();
          focusedIndex.value = Math.max(focusedIndex.value - 1, 0);
          showReactionPicker.value = false;
          break;
        }
        case "e": {
          e.preventDefault();
          advanceToNext();
          break;
        }
        case "r": {
          e.preventDefault();
          showReactionPicker.value = !showReactionPicker.value;
          break;
        }
        case "c": {
          e.preventDefault();
          const focused = items[focusedIndex.value];
          if (focused) {
            if (openCommentId.value === focused.id) {
              openCommentId.value = null;
            } else {
              openCommentId.value = focused.id;
            }
          }
          break;
        }
        case "u": {
          e.preventDefault();
          handleUndo();
          break;
        }
        case "1":
        case "2":
        case "3":
        case "4":
        case "5":
        case "6": {
          if (showReactionPicker.value) {
            e.preventDefault();
            const emojis = ["👍", "❤️", "🎉", "🔥", "👀", "🚀"];
            const emoji = emojis[Number.parseInt(e.key) - 1];
            const focused = items[focusedIndex.value];
            if (focused && emoji) {
              handleQuickReaction(focused, emoji);
            }
            showReactionPicker.value = false;
          }
          break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // Empty deps: handler reads all state via signals (.value), not React state,
    // so it never goes stale and doesn't need re-binding on re-render.
  }, []);

  // Quick reaction handler
  const handleQuickReaction = async (item: FocusableItem, emoji: string) => {
    const data = currentData.value;
    let targetType: "form_answer" | "priority";
    let targetId: string;

    if (item.type === "priority") {
      targetType = "priority";
      targetId = item.priority.id;
    } else {
      if (!item.answer?.id) return;
      targetType = "form_answer";
      targetId = item.answer.id;
    }

    try {
      const result = await rpc.reactions.toggle({
        workspaceId: orgId,
        targetType,
        targetId,
        emoji: emoji as "👍" | "👏" | "🎉" | "❤️" | "💡" | "🙌",
      });

      // Optimistically update the relevant reactions in currentData
      const reactionsMap =
        targetType === "form_answer"
          ? { ...data.answerReactions }
          : { ...data.priorityReactions };

      const existing = reactionsMap[targetId] ?? [];
      if (result.added) {
        const existingReaction = existing.find((r) => r.emoji === emoji);
        if (existingReaction) {
          reactionsMap[targetId] = existing.map((r) =>
            r.emoji === emoji
              ? { ...r, count: r.count + 1, userReacted: true }
              : r,
          );
        } else {
          reactionsMap[targetId] = [
            ...existing,
            { emoji, count: 1, memberNames: ["You"], userReacted: true },
          ];
        }
      } else {
        const existingReaction = existing.find((r) => r.emoji === emoji);
        if (existingReaction && existingReaction.count <= 1) {
          reactionsMap[targetId] = existing.filter((r) => r.emoji !== emoji);
        } else {
          reactionsMap[targetId] = existing.map((r) =>
            r.emoji === emoji
              ? { ...r, count: r.count - 1, userReacted: false }
              : r,
          );
        }
      }

      if (targetType === "form_answer") {
        currentData.value = { ...data, answerReactions: reactionsMap };
      } else {
        currentData.value = { ...data, priorityReactions: reactionsMap };
      }
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
    }
  };

  const totalCount = queue.value.length;
  const progressPercent =
    totalCount > 0
      ? Math.round(
          ((currentIndex.value + (allDone.value ? 1 : 0)) / totalCount) * 100,
        )
      : 0;

  // ============================================================================
  // All Done Screen
  // ============================================================================

  if (allDone.value) {
    return (
      <div
        class="fixed inset-0 z-50 bg-surface-page flex flex-col"
        data-brand={brandColor}
      >
        <ErrorDialog />
        <div class="flex-1 flex items-center justify-center">
          <div class="text-center animate-fade-in-up">
            <div class="text-6xl mb-4">&#127881;</div>
            <h1 class="text-3xl font-bold text-text mb-2">All caught up!</h1>
            <p class="text-text-muted mb-8">
              You've reviewed all {totalCount} check-in
              {totalCount !== 1 ? "s" : ""} for this week.
            </p>
            <div class="flex items-center justify-center gap-4">
              <a
                href={`/orgs/${orgId}/team?week=${weekKey}`}
                class="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-[var(--brand-600)] text-white hover:bg-[var(--brand-700)] transition-colors"
              >
                Back to Team
              </a>
              {undoStack.value.length > 0 && (
                <button
                  type="button"
                  onClick={handleUndo}
                  class="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
                >
                  <IcoUndo class="size-4" />
                  Undo last
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Main Review Screen
  // ============================================================================

  const data = currentData.value;
  const queueItem = queue.value[currentIndex.value]!;
  const items = focusableItems.value;

  return (
    <div
      class="fixed inset-0 z-50 bg-surface-page flex flex-col"
      data-brand={brandColor}
    >
      <ErrorDialog />

      {/* ================================================================ */}
      {/* Header */}
      {/* ================================================================ */}
      <header class="sticky top-0 z-10 bg-surface border-b border-border px-4 py-3">
        <div class="max-w-2xl mx-auto flex items-center gap-4">
          {/* Back button */}
          <button
            type="button"
            onClick={exitReview}
            class="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text transition-colors flex-shrink-0"
          >
            <IcoChevronLeft class="size-4" />
            Team
          </button>

          {/* Title */}
          <div class="flex-1 min-w-0 text-center">
            <p class="text-sm font-semibold text-text truncate">
              {queueItem.memberName}
            </p>
            <p class="text-xs text-text-muted truncate">
              {queueItem.formTitle}
            </p>
          </div>

          {/* Counter */}
          <span class="text-sm text-text-muted font-medium flex-shrink-0 tabular-nums">
            {currentIndex.value + 1}/{totalCount}
          </span>
        </div>

        {/* Progress bar */}
        <div class="max-w-2xl mx-auto mt-2">
          <div class="h-1 bg-surface-muted rounded-full overflow-hidden">
            <div
              class="h-full bg-[var(--brand-500)] rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </header>

      {/* ================================================================ */}
      {/* Scrollable Body */}
      {/* ================================================================ */}
      <main
        ref={mainContainerRef}
        class="flex-1 overflow-y-auto"
        key={animationKey.value}
      >
        <div class="max-w-2xl mx-auto px-4 py-8 animate-slide-in-left">
          {/* Member header */}
          <header class="flex items-start justify-between gap-4 pb-4 border-b border-border mb-8">
            <div class="flex items-center gap-4">
              {data.submission.memberProfilePictureUrl ? (
                <img
                  src={data.submission.memberProfilePictureUrl}
                  alt=""
                  class="size-14 rounded-full object-cover"
                />
              ) : (
                <div class="flex items-center justify-center size-14 rounded-full bg-[var(--brand-100)] dark:bg-[var(--brand-900)]/30 text-[var(--brand-600)] dark:text-[var(--brand-400)] text-xl font-semibold">
                  {(data.submission.memberName ?? "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h1 class="text-xl font-semibold text-text">
                  {data.submission.memberName}
                </h1>
                <p class="text-sm text-text-muted">
                  {data.form.title || "Check-in"}
                </p>
                {data.form.periodStart && (
                  <p class="text-xs text-text-subtle">
                    {formatPeriodRange(
                      data.form.periodStart,
                      data.form.periodEnd,
                    )}
                  </p>
                )}
              </div>
            </div>
            <div class="text-right text-sm">
              {data.submission.submittedAt && (
                <p class="text-text-muted">
                  Submitted on{" "}
                  <span class="text-text font-medium">
                    {formatDateWithTime(new Date(data.submission.submittedAt))}
                  </span>
                </p>
              )}
              {data.submission.managerReviewedBy && (
                <p class="text-green-600 dark:text-green-400 flex items-center justify-end gap-1 mt-1">
                  <IcoCheck class="size-3.5" />
                  {data.submission.managerReviewedBy} reviewed
                </p>
              )}
            </div>
          </header>

          {/* Rating answers → Recognition → Other answers → Priorities */}
          {(() => {
            const ratingQs = data.questions.filter((q) => q.type === "rating");
            const otherQs = data.questions.filter((q) => q.type !== "rating");

            const renderAnswerCard = (question: (typeof data.questions)[0]) => {
              const answer =
                data.submission.answers.find(
                  (a) => a.questionId === question.id,
                ) ?? null;
              const itemId = `answer-${question.id}`;
              const focusIdx = items.findIndex((i) => i.id === itemId);
              const isFocused = focusIdx === focusedIndex.value;

              return (
                <div key={question.id}>
                  <ReviewFocusWrapper
                    id={itemId}
                    isFocused={isFocused}
                    itemRefs={itemRefs}
                  >
                    <AnswerCard
                      question={question}
                      answer={answer}
                      workspaceId={orgId}
                      currentMemberId={data.currentMemberId}
                      initialReactions={
                        answer?.id ? data.answerReactions[answer.id] : undefined
                      }
                      commentCount={
                        answer?.id
                          ? (data.answerCommentCounts[answer.id] ?? 0)
                          : 0
                      }
                      isCommentOpen={openCommentId.value === itemId}
                      onToggleComment={() => {
                        openCommentId.value =
                          openCommentId.value === itemId ? null : itemId;
                      }}
                      ratingTrend={
                        question.type === "rating"
                          ? data.ratingTrends[question.id]
                          : undefined
                      }
                    />
                    {isFocused && showReactionPicker.value && answer?.id && (
                      <InlineReactionPicker
                        onSelect={(emoji) => {
                          const focused = items[focusedIndex.value];
                          if (focused) handleQuickReaction(focused, emoji);
                          showReactionPicker.value = false;
                        }}
                      />
                    )}
                  </ReviewFocusWrapper>
                </div>
              );
            };

            return (
              <>
                {/* Rating answers */}
                {ratingQs.length > 0 && (
                  <section>
                    <div class="space-y-4">
                      {ratingQs.map(renderAnswerCard)}
                    </div>
                  </section>
                )}

                {/* Values Recognition — right after rating */}
                {data.recognitions.length > 0 && (
                  <section class="space-y-4">
                    <h2 class="text-2xl font-semibold text-text">
                      Values Recognition
                    </h2>
                    {data.recognitions.map((rec) => (
                      <ReviewRecognitionCard
                        key={rec.id}
                        recognition={rec}
                        orgId={orgId}
                        currentMemberId={data.currentMemberId}
                        commentCount={
                          data.recognitionCommentCounts[rec.id] ?? 0
                        }
                        initialReactions={data.recognitionReactions[rec.id]}
                      />
                    ))}
                  </section>
                )}

                {/* Other question answers */}
                {otherQs.length > 0 && (
                  <section>
                    <h2 class="text-2xl font-semibold text-text mb-6">
                      Questions
                    </h2>
                    <div class="space-y-4">{otherQs.map(renderAnswerCard)}</div>
                  </section>
                )}
              </>
            );
          })()}

          {/* Priority sections */}
          {data.priorities && (
            <>
              {data.priorities.previousPeriod.length > 0 && (
                <div class="mb-6">
                  <PrioritySection
                    title={`${data.submission.memberName}'s Previous Priorities`}
                    description="What they planned to accomplish"
                  >
                    {data.priorities.previousPeriod.map((priority) => {
                      const itemId = `prev-${priority.id}`;
                      const focusIdx = items.findIndex((i) => i.id === itemId);
                      const isFocused = focusIdx === focusedIndex.value;
                      return (
                        <ReviewFocusWrapper
                          key={priority.id}
                          id={itemId}
                          isFocused={isFocused}
                          itemRefs={itemRefs}
                        >
                          <PriorityItem
                            priority={priority}
                            isFromPrevious={true}
                            readOnly={true}
                            workspaceId={orgId}
                            currentMemberId={data.currentMemberId}
                            commentCount={
                              data.priorityCommentCounts[priority.id] ?? 0
                            }
                            reactions={data.priorityReactions[priority.id]}
                          />
                          {isFocused && showReactionPicker.value && (
                            <InlineReactionPicker
                              onSelect={(emoji) => {
                                const focused = items[focusedIndex.value];
                                if (focused)
                                  handleQuickReaction(focused, emoji);
                                showReactionPicker.value = false;
                              }}
                            />
                          )}
                        </ReviewFocusWrapper>
                      );
                    })}
                  </PrioritySection>
                </div>
              )}

              {data.priorities.currentPeriod.length > 0 && (
                <div class="mb-6">
                  <PrioritySection
                    title={`${data.submission.memberName}'s Current Priorities`}
                    description="What they're working on this period"
                  >
                    {data.priorities.currentPeriod.map((priority) => {
                      const itemId = `curr-${priority.id}`;
                      const focusIdx = items.findIndex((i) => i.id === itemId);
                      const isFocused = focusIdx === focusedIndex.value;
                      return (
                        <ReviewFocusWrapper
                          key={priority.id}
                          id={itemId}
                          isFocused={isFocused}
                          itemRefs={itemRefs}
                        >
                          <PriorityItem
                            priority={priority}
                            readOnly={true}
                            workspaceId={orgId}
                            currentMemberId={data.currentMemberId}
                            commentCount={
                              data.priorityCommentCounts[priority.id] ?? 0
                            }
                            reactions={data.priorityReactions[priority.id]}
                          />
                          {isFocused && showReactionPicker.value && (
                            <InlineReactionPicker
                              onSelect={(emoji) => {
                                const focused = items[focusedIndex.value];
                                if (focused)
                                  handleQuickReaction(focused, emoji);
                                showReactionPicker.value = false;
                              }}
                            />
                          )}
                        </ReviewFocusWrapper>
                      );
                    })}
                  </PrioritySection>
                </div>
              )}
            </>
          )}

          {/* Bottom spacer for footer */}
          <div class="h-24" />
        </div>
      </main>

      {/* ================================================================ */}
      {/* Footer */}
      {/* ================================================================ */}
      <footer class="sticky bottom-0 z-10 bg-surface border-t border-border px-4 py-3">
        <div class="max-w-2xl mx-auto flex items-center justify-between">
          {/* Keyboard hints */}
          <div class="hidden sm:flex items-center gap-3 text-xs text-text-subtle">
            <span>
              <kbd class="px-1.5 py-0.5 rounded bg-surface-muted text-text-muted font-mono text-[10px]">
                j
              </kbd>
              <kbd class="px-1.5 py-0.5 rounded bg-surface-muted text-text-muted font-mono text-[10px] ml-0.5">
                k
              </kbd>{" "}
              navigate
            </span>
            <span>
              <kbd class="px-1.5 py-0.5 rounded bg-surface-muted text-text-muted font-mono text-[10px]">
                e
              </kbd>{" "}
              review
            </span>
            <span>
              <kbd class="px-1.5 py-0.5 rounded bg-surface-muted text-text-muted font-mono text-[10px]">
                r
              </kbd>{" "}
              react
            </span>
            <span>
              <kbd class="px-1.5 py-0.5 rounded bg-surface-muted text-text-muted font-mono text-[10px]">
                c
              </kbd>{" "}
              comment
            </span>
          </div>

          {/* Action button */}
          <button
            type="button"
            onClick={advanceToNext}
            disabled={markingReviewed.value}
            class="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50"
          >
            {markingReviewed.value ? (
              <IcoSpinner class="size-4 animate-spin" />
            ) : (
              <IcoCheck class="size-4" />
            )}
            Reviewed & Next
            <IcoChevronRight class="size-4" />
          </button>
        </div>
      </footer>

      {/* ================================================================ */}
      {/* Undo Toast */}
      {/* ================================================================ */}
      {showUndoToast.value && undoStack.value.length > 0 && (
        <div class="fixed bottom-20 right-6 z-50 animate-fade-in-up">
          <div class="flex items-center gap-3 px-4 py-3 rounded-lg bg-text text-surface shadow-lg">
            <IcoCheck class="size-4 flex-shrink-0" />
            <span class="text-sm font-medium">Marked as reviewed</span>
            <button
              type="button"
              onClick={handleUndo}
              class="text-sm font-semibold underline hover:no-underline ml-2"
            >
              Undo
            </button>
            <button
              type="button"
              onClick={() => {
                showUndoToast.value = false;
              }}
              class="ml-1 opacity-60 hover:opacity-100"
            >
              <IcoX class="size-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ReviewFocusWrapper
// ============================================================================

function ReviewFocusWrapper({
  id,
  isFocused,
  itemRefs,
  children,
}: {
  id: string;
  isFocused: boolean;
  itemRefs: { current: Map<string, HTMLDivElement> };
  children: preact.ComponentChildren;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      itemRefs.current.set(id, ref.current);
    }
    return () => {
      itemRefs.current.delete(id);
    };
  }, [id]);

  return (
    <div
      ref={ref}
      class={`relative transition-all duration-150 rounded-lg ${
        isFocused
          ? "ring-2 ring-[var(--brand-500)]/50 bg-[var(--brand-50)]/30 dark:bg-[var(--brand-900)]/10 pl-4 border-l-3 border-l-[var(--brand-500)]"
          : "pl-0"
      }`}
    >
      {children}
    </div>
  );
}

// ============================================================================
// AnswerCard (read-only display for review mode)
// ============================================================================

function AnswerCard({
  question,
  answer,
  workspaceId,
  currentMemberId,
  initialReactions,
  commentCount,
  isCommentOpen,
  onToggleComment,
  ratingTrend,
}: {
  question: QuestionInfo;
  answer: FormAnswer | null;
  workspaceId: string;
  currentMemberId: string;
  initialReactions?: ReactionSummary[];
  commentCount: number;
  isCommentOpen: boolean;
  onToggleComment: () => void;
  ratingTrend?: RatingTrendPoint[];
}) {
  const localCommentCount = useSignal(commentCount);

  const hasCommentsFeature = answer?.id && workspaceId && currentMemberId;

  return (
    <article class="group">
      <h3
        class="text-sm font-semibold text-text-muted mb-2 prose prose-sm max-w-none [&_p]:m-0 [&_p]:text-sm [&_p]:font-semibold [&_p]:text-text-muted"
        dangerouslySetInnerHTML={{ __html: question.title }}
      />

      {question.type === "short_answer" ? (
        answer?.textAnswer ? (
          <div>
            <div
              class="prose prose-base max-w-none text-text [&_p]:m-0 [&_p]:leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: decodeMentionsToHtml(answer.textAnswer),
              }}
            />
          </div>
        ) : (
          <p class="text-text-muted italic text-sm">No response</p>
        )
      ) : answer?.ratingAnswer != null ? (
        <div>
          <RatingTrendChart
            data={
              ratingTrend && ratingTrend.length > 0
                ? ratingTrend
                : [
                    {
                      periodStart: new Date().toISOString(),
                      rating: answer.ratingAnswer,
                    },
                  ]
            }
            max={question.ratingMax}
          />
          {answer?.comment && (
            <p class="mt-2 text-sm text-text-muted italic">{answer.comment}</p>
          )}
        </div>
      ) : (
        <p class="text-text-muted italic text-sm">No response</p>
      )}

      {/* Reaction + Comment on same row */}
      {hasCommentsFeature && (
        <div class="mt-2 flex items-center gap-3">
          <ReactionBar
            workspaceId={workspaceId}
            targetType="form_answer"
            targetId={answer.id!}
            initialReactions={initialReactions}
          />
          <CommentButton
            count={localCommentCount.value}
            isOpen={isCommentOpen}
            onClick={onToggleComment}
          />
        </div>
      )}
      {hasCommentsFeature && isCommentOpen && (
        <div class="mt-2 rounded-lg bg-surface-muted/50 dark:bg-surface-muted/30">
          <div class="p-4">
            <CommentThread
              workspaceId={workspaceId}
              targetType="form_answer"
              targetId={answer.id!}
              currentMemberId={currentMemberId}
              autoFocus
              onCommentCountChange={(count) => {
                localCommentCount.value = count;
              }}
            />
          </div>
        </div>
      )}
    </article>
  );
}

// ============================================================================
// InlineReactionPicker (keyboard-driven)
// ============================================================================

function InlineReactionPicker({
  onSelect,
}: {
  onSelect: (emoji: string) => void;
}) {
  const emojis = ["👍", "❤️", "🎉", "🔥", "👀", "🚀"];

  return (
    <div class="mt-2 animate-fade-in-up">
      <div class="inline-flex items-center gap-1 p-1.5 bg-surface border border-border rounded-lg shadow-sm">
        {emojis.map((emoji, idx) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onSelect(emoji)}
            class="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-surface-muted transition-colors text-base"
          >
            <span>{emoji}</span>
            <span class="text-[10px] text-text-subtle font-mono">
              {idx + 1}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// ReviewRecognitionCard — stateful recognition card for review page
// ============================================================================

function ReviewRecognitionCard({
  recognition,
  orgId,
  currentMemberId,
  commentCount: initialCommentCount,
  initialReactions,
}: {
  recognition: ValueRecognition;
  orgId: string;
  currentMemberId: string;
  commentCount: number;
  initialReactions?: ReactionSummary[];
}) {
  const showComments = useSignal(false);
  const localCommentCount = useSignal(initialCommentCount);

  useEffect(() => {
    localCommentCount.value = initialCommentCount;
  }, [initialCommentCount]);

  return (
    <div class="rounded-xl border border-border bg-surface">
      <div class="p-4 space-y-3">
        <div class="space-y-1.5">
          <div class="flex flex-wrap items-center gap-1.5 text-sm">
            {recognition.author.profilePictureUrl ? (
              <img
                src={recognition.author.profilePictureUrl}
                alt=""
                class="size-5 rounded-full object-cover"
              />
            ) : (
              <span class="size-5 rounded-full bg-surface-muted flex items-center justify-center text-[10px] font-semibold text-text-muted">
                {recognition.author.name?.charAt(0).toUpperCase()}
              </span>
            )}
            <span class="font-medium text-text">{recognition.author.name}</span>
            <span class="text-text-muted">recognized</span>
            {recognition.recipients.map((r) => (
              <span
                key={r.memberId}
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-surface-hover text-text"
              >
                {r.profilePictureUrl ? (
                  <img
                    src={r.profilePictureUrl}
                    alt=""
                    class="size-4 rounded-full object-cover"
                  />
                ) : (
                  <span class="size-4 rounded-full bg-surface-muted flex items-center justify-center text-[9px] font-semibold text-text-muted">
                    {r.name?.charAt(0).toUpperCase()}
                  </span>
                )}
                {r.name}
              </span>
            ))}
          </div>
          <div class="flex flex-wrap gap-1.5">
            {recognition.values.map((v) => (
              <span
                key={v.id}
                class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--brand-100)] text-[var(--brand-700)]"
              >
                {v.name}
              </span>
            ))}
          </div>
        </div>
        {recognition.message && (
          <p class="text-sm text-text whitespace-pre-wrap">
            {recognition.message}
          </p>
        )}
      </div>
      <div class="px-4 pb-3 flex items-center gap-3">
        <ReactionBar
          workspaceId={orgId}
          targetType="value_recognition"
          targetId={recognition.id}
          initialReactions={initialReactions}
        />
        <CommentButton
          count={localCommentCount.value}
          isOpen={showComments.value}
          onClick={() => {
            showComments.value = !showComments.value;
          }}
        />
      </div>
      {showComments.value && (
        <div class="border-t border-border px-4 py-3">
          <CommentThread
            workspaceId={orgId}
            targetType="value_recognition"
            targetId={recognition.id}
            currentMemberId={currentMemberId}
            onCommentCountChange={(count) => {
              localCommentCount.value = count;
            }}
          />
        </div>
      )}
    </div>
  );
}
